import api from './api';

export type FlyerFormat = 'pdf' | 'linkedin' | 'square' | 'story';

export const FLYER_FORMATS: FlyerFormat[] = ['pdf', 'linkedin', 'square', 'story'];

export interface SeriesFormatSummary {
  id: string;
  name: string;
  title: string;
  footer?: string | null;
  flyerBrandColor?: string | null;
  flyerLogoUrl?: string | null;
}

export interface ContentBriefSummary {
  id: string;
  theme: string;
  challenge: string;
  learning: string;
  fullBrief?: string | null;
}

export interface ReadyPostIdea {
  id: string;
  title: string;
  topic: string;
  description?: string | null;
  status: string;
  seriesFormat?: SeriesFormatSummary | null;
  contentBrief?: ContentBriefSummary | null;
}

export interface Flyer {
  id: string;
  postIdeaId: string;
  seriesFormatId?: string | null;
  userId: string;
  status: 'draft' | 'ready' | 'posted' | 'failed';
  pdfPath?: string | null;
  linkedinImagePath?: string | null;
  instaSquarePath?: string | null;
  instaStoryPath?: string | null;
  linkedinPostId?: string | null;
  linkedinPostUrl?: string | null;
  renderError?: string | null;
  createdAt: string;
  updatedAt: string;
  postIdea?: ReadyPostIdea;
  seriesFormat?: SeriesFormatSummary | null;
}

export interface GenerateFlyerInput {
  postIdeaId: string;
  formats?: FlyerFormat[];
}

class FlyerService {
  async listReadyPostIdeas(): Promise<ReadyPostIdea[]> {
    const { data } = await api.get('/flyers/ready-post-ideas');
    return data;
  }

  async listFlyers(): Promise<Flyer[]> {
    const { data } = await api.get('/flyers');
    return data;
  }

  async generate(input: GenerateFlyerInput): Promise<Flyer> {
    const { data } = await api.post('/flyers/generate', input);
    return data;
  }

  async postToLinkedIn(id: string, caption?: string): Promise<Flyer> {
    const { data } = await api.post(`/flyers/${id}/post-linkedin`, { caption });
    return data;
  }

  /**
   * Download URL for the given format. Used as `src` for preview images and
   * as href for <a download>. Token is sent via axios interceptor only, so
   * direct URL access relies on the cookie auth path when present. If the
   * deployment uses bearer-only auth, wrap the fetch + blob flow instead.
   */
  downloadUrl(id: string, format: FlyerFormat): string {
    const base = process.env.REACT_APP_API_URL || '/api';
    return `${base}/flyers/${id}/download/${format}`;
  }

  async fetchBlob(id: string, format: FlyerFormat): Promise<Blob> {
    const response = await api.get(`/flyers/${id}/download/${format}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async triggerDownload(id: string, format: FlyerFormat, filename?: string) {
    const blob = await this.fetchBlob(id, format);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const ext = format === 'pdf' ? 'pdf' : 'png';
    link.setAttribute('download', filename ?? `flyer-${id}-${format}.${ext}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}

const flyerService = new FlyerService();
export default flyerService;
